import { pct } from '@shared/format';
import type { Trap } from '@shared/types';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/ru';
import { downloadImage, renderShareCard, shareResult } from '../lib/share';
import { useController } from '../state/context';
import { Trophy } from './Icons';

const trapResult = (trap: Trap) =>
  trap.role === 't' ? `${trap.got} ✓` : trap.role === 'd' ? `${trap.got} — ${t.decoyMark}` : `${trap.got} 💥`;

export function EndCard() {
  const c = useController();
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const image = useRef<Blob | null>(null);
  const game = c.game.value;
  const over = !!game && game.status !== 'play';
  const gameId = game?.id;

  // Итог появляется под полем — подводим его в поле зрения, чтобы момент «победа/взрыв» не остался за экраном
  useEffect(() => {
    if (!over) return;
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    box.current?.scrollIntoView?.({ behavior: calm ? 'auto' : 'smooth', block: 'nearest' });
  }, [over]);

  // Картинку готовим заранее, а не по клику: Safari разрешает navigator.share только сразу после нажатия
  useEffect(() => {
    image.current = null;
    setReady(false);
    const card = over ? c.shareCard() : null;
    if (!card) return;
    let stale = false;
    void renderShareCard(card).then((blob) => {
      if (stale) return;
      image.current = blob;
      setReady(blob !== null);
    });
    return () => {
      stale = true;
    };
  }, [over, gameId, c]);

  if (!game || !over) return null;

  const win = game.status === 'win';
  const { analysis } = game;
  const [emoji = '', ...words] = (win ? (game.stars === 3 ? t.win.perfect : t.win.normal) : t.lose).split(
    ' ',
  );
  const sub = win ? t.winSub(game.heat) : game.boom === 'picked' ? t.loseSub.picked : t.loseSub.heat;

  const share = async () => {
    const result = await shareResult({
      text: c.shareText(),
      url: c.shareLink(),
      title: t.shareTitle,
      image: image.current,
    });
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div class={win ? 'end win' : 'end lose'} data-testid="end-card" ref={box}>
      <div class="end-halo" aria-hidden="true" />
      {/* эмодзи отдельно: заливка текста градиентом (background-clip) сделала бы его серым пятном */}
      <div class="big">
        <span class="big-emoji">{emoji}</span> <span class="big-text">{words.join(' ')}</span>
      </div>
      {win && (
        <div class="stars" role="img" aria-label={`${game.stars} из 3 звёзд`}>
          {[1, 2, 3].map((n) => (
            <span class={n <= game.stars ? 'star on' : 'star'} style={{ '--k': n - 1 }} key={n}>
              {n <= game.stars ? '★' : '☆'}
            </span>
          ))}
        </div>
      )}
      <div class="sub">{sub}</div>
      {win && c.record.value && (
        <div class="record" data-testid="record">
          <Trophy />
          {t.record}
        </div>
      )}
      <div class="recap" role="img" aria-label={t.turnsCount(game.turns.length)}>
        {game.turns.map((turn, k) => (
          <i class={turn.role} style={{ '--k': k }} key={`${turn.clue}-${turn.pick}`} />
        ))}
      </div>
      <div class="btns">
        <button type="button" class="primary shine" onClick={() => void share()}>
          {copied ? t.shared : t.share}
        </button>
        <button type="button" onClick={() => void c.playAgain()}>
          {t.again}
        </button>
        <button type="button" onClick={() => void c.stepBoard(1)}>
          {t.nextBoard}
        </button>
      </div>
      <button
        type="button"
        class="link-btn"
        data-testid="save-image"
        disabled={!ready}
        onClick={() => image.current && downloadImage(image.current)}
      >
        {t.saveImage}
      </button>
      {analysis && (
        <details data-testid="analysis">
          <summary>{t.howToPlay}</summary>
          {analysis.bestPath.length > 0 && (
            <>
              <p style={{ margin: '12px 0 4px' }}>
                <b>{t.bestPath(analysis.bestHeat)}</b>
              </p>
              {analysis.bestPath.map((step) => (
                <div class="row" key={step.clue}>
                  «<b>{step.clue}</b>» → {step.word}{' '}
                  <span style={{ color: 'var(--bomb)' }}>+{pct(step.heat)}</span>
                </div>
              ))}
            </>
          )}
          {analysis.traps.length > 0 && (
            <>
              <p style={{ margin: '14px 0 4px' }}>
                <b>{t.traps}</b>
              </p>
              {analysis.traps.map((trap) => (
                <div class="row" key={trap.clue}>
                  «<b>{trap.clue}</b>» → {trapResult(trap)}{' '}
                  <span style={{ color: 'var(--bomb)' }}>
                    {trap.role === 'b' || trap.heat >= 1 ? t.explosion : `+${pct(trap.heat)}`}
                  </span>
                </div>
              ))}
            </>
          )}
        </details>
      )}
    </div>
  );
}
